<template>
  <div class="StorageExplorerList DataUpload__Content flex flex-col bg-white rounded-md gap-2 overflow-scroll mb-6 relative">
    <storage-explorer-filters 
      v-model="searchQuery"
      :current-sort-dir="currentSortDir"
      :current-sort-order="currentSortOrder"
      :sort-filters="sortFilters"
      @update:sort-order="sort"
    />
    <div 
      class="px-2 pb-2"
      :class="view === 'grid' ? 'grid grid-cols-3 sm:grid-cols-6 gap-2' : 'DataUpload__List flex flex-col'"
    >
      <template v-if="view === 'grid'">
        <button
          v-if="currentDirectoryPath !== '/'"
          class="w-full overflow-hidden cursor-pointer text-sm text-gray-300 hover:text-gray-800"
          @click.prevent.stop="goBack"
        >
          <div class="w-full flex items-center justify-center" style="aspect-ratio: 1;">
            <aw-icon icon="arrow-back" class="w-8 h-8" />
          </div>
          <span class="block max-w-full text-sm truncate">..</span>
        </button>
        <template v-if="displayedData.length > 0">
          <storage-explorer-item-box
            v-for="(item, i) in displayedData"
            :key="i"
            :is-data-storage="false"
            :is-selected="selectedItems.has(item.id)"
            :item="item"
            :index="i"
            @select="selectItem"
            @unselect="unselectItem"
            @open-directory="loadData"
          />
        </template>
      </template>
      <template v-else-if="view === 'list'">
        <button
          v-if="currentDirectoryPath !== '/'"
          class="StorageExplorerItemRow h-12 flex flex-row justify-start items-center gap-2 border-t-1 border-gray-300 cursor-pointer text-sm text-gray-300 hover:text-gray-800"
          @click.prevent.stop="goBack"
        >
          <div class="h-full flex items-center justify-center" style="aspect-ratio: 1;">
            <aw-icon icon="arrow-back" class="w-6 h-6" />
          </div>
          <span class="inline-block max-w-full text-sm truncate">..</span>
        </button>
        <template v-if="displayedData.length > 0">
          <storage-explorer-item-row
            v-for="(item, i) in displayedData"
            :key="i"
            :is-data-storage="false"
            :is-selected="selectedItems.has(item.id)"
            :item="item"
            :index="i"
            @select="selectItem"
            @unselect="unselectItem"
            @open-directory="loadData"
          />
        </template>
      </template>
    </div>
    <div ref="loadMoreEl" class="w-full col-span-3 sm:col-span-6" v-show="showLoadMoreEl">
      &nbsp;
    </div>
    <div 
      v-if="isLoadingInitial" 
      class="absolute inset-0 flex items-center justify-center h-full w-full px-2 mb-4"
    >
      <div class="absolute inset-0 h-full w-full opacity-60 bg-white" />
      <aw-icon icon="loading" class="animate-spin w-6 h-6 md:w-10 md:h-10 leading-tight text-bc-blue" />
    </div>
    <div v-if="isLoadingMore" class="flex items-center justify-center px-2 mb-4">
      <aw-icon icon="loading" class="animate-spin w-6 h-6 md:w-10 md:h-10 leading-tight text-bc-blue" />
    </div>
    <span 
      v-if="isEmptyDirectory" 
      class="absolute inset-0 flex items-center justify-center h-full px-2 mb-4 text-gray-300 text-lg pointer-events-none"
    >
      {{ $t('Folder is empty') }}
    </span>
  </div>
</template>

<script>
import { DriveApi } from '@/frontapp/test-api'
import debounce from 'lodash/debounce'
import StorageExplorerSelected from '../../mixins/StorageExplorerSelected'

const driveApi = new DriveApi({}, '/api/v2')
let lastSearch

export default {
  mixins: [
    StorageExplorerSelected,
  ],
  emits: ['unauthenticated'],
  props: {
    type: String,
    view: String,
  },
  data() {
    return {
      sortFilters: [
        {
          action: 'name',
          text: 'By name'
        },
        {
          action: 'modified',
          text: 'By modified'
        },
        {
          action: 'size',
          text: 'By size'
        },
      ],
      loadMoreObserver: null,
      limit: 50,
      offset: 0,
      displayedData: [],
      currentDirectoryPath: '/',
      loading: false,
      backStack: [],
      debouncedSearch: null,
      searchQuery: '',
      currentSortDir: 'desc',
      currentSortOrder: 'modified',
    }
  },
  computed: {
    showLoadMoreEl() {
      return this.displayedData.length % this.limit > 0 || this.loading ? false : true
    },
    isLoadingInitial() {
      return !this.isLoadingMore && this.loading
    },
    isLoadingMore() {
      return this.displayedData.length > 0 && this.loading && this.showLoadMoreEl
    },
    isEmptyDirectory() {
      return this.displayedData.length === 0 && !this.isLoadingInitial
    },
  },
  methods: {
    goBack() {
      this.backStack.pop() // current directory
      this.loadData(this.backStack.pop())
    },
    async loadData(path, clearSelected = true, mergeData = false) {
      this.loading = true
      if (clearSelected) {
        this.selectedItems.clear()
      }

      if (!this.backStack.length || this.backStack[this.backStack.length - 1] !== path) {
        this.backStack.push(path)
      }

      let sortOrderDir = this.currentSortOrder

      if (this.currentSortDir === 'desc') {
        sortOrderDir = sortOrderDir + ':' + 'desc'
      }

      try {
        const result = await driveApi.driveList(this.type, path, this.searchQuery, sortOrderDir, this.limit, this.offset)

        if (!mergeData) {
          this.displayedData = this.mapRequestDataToBc(result.data)
        } else {
          this.displayedData = [
            ...this.displayedData,
            ...this.mapRequestDataToBc(result.data)
          ]
        }

        this.currentDirectoryPath = path
      } catch (exception) {
        if (exception.response?.status === 401) {
          this.$emit('unauthenticated', this.type)
        }
      } finally {
        this.loading = false
      }
    },
    mapRequestDataToBc(data) {
      return data.map(item => {
        const lastDotIndex = item.name.lastIndexOf('.')
        
        return {
          id: item.path,
          size: item.fileSize,
          name: item.name,
          uploaded: item.lastModified,
          ext: lastDotIndex !== -1
            ? item.name.substring(lastDotIndex, item.name.length)
            : '',
          type: item.type
        }
      })
    },
    async sort(sortOrder) {
      if (this.currentSortOrder === sortOrder) {
        if (this.currentSortDir === 'desc') {
          this.currentSortDir = 'asc'
        } else {
          this.currentSortDir = 'desc'
        }
      } else {
        this.currentSortOrder = sortOrder
        this.currentSortDir = 'desc'
      }

      await this.loadData('/', true)
    },
    /*
    restartLoadMoreObserver() {
      this.loadMoreObserver.unobserve(this.$refs.loadMoreEl)
      this.$nextTick(() => {
        this.loadMoreObserver.observe(this.$refs.loadMoreEl)
      })
    },
    handleIntersect(entries) {
      if (this.loading === false && entries[0].isIntersecting === true) {
        this.offset += this.limit
        this.loadData(this.currentDirectoryPath, false, true)
      }
    },
    */
  },
  mounted() {
    this.loadData('/')
    /*
    this.loadMoreObserver = new IntersectionObserver(this.handleIntersect)
    this.$nextTick(() => {
      if (this.$refs.loadMoreEl instanceof HTMLElement) {
        this.loadMoreObserver.observe(this.$refs.loadMoreEl)
      }
    })
    */
  },
  /*
  beforeUnmount() {
    this.loadMoreObserver.unobserve(this.$refs.loadMoreEl)
    this.loadMoreObserver = null
  },
  */
  watch: {
    /*
    view() {
      this.restartLoadMoreObserver()
    },
    */
    searchQuery(newVal) {
      if (this.debouncedSearch === null) {
        this.debouncedSearch = debounce((search) => {
          if (search === lastSearch) {
            return
          }

          lastSearch = search
          this.loadData(this.currentDirectoryPath, true)
        }, 500)
      }

      this.debouncedSearch(newVal)
    }
  }
}
</script>

<style scoped>
.StorageExplorerList {
  height: 100%;
}
</style>
